import { FC, useState, useEffect } from 'react';

import { Modal, Button, Radio, RadioGroup } from '../../shared/ui';

import { IClient, IConsultation } from '../../store/types';
import { getDate, getTime } from '../../utils/getTime';

type DeleteScope = 'one' | 'this_and_future' | 'series';

/** Встреча из повторяющейся серии — показываем выбор области удаления в календаре. */
function isRecurringConsultation(data: IConsultation): boolean {
  const ext = data as IConsultation & {
    is_recurring?: boolean;
    recurrence_series_id?: string | number | null;
  };
  if (ext.is_recurring === true) return true;
  if (ext.recurrence != null && ext.recurrence !== '') return true;
  if (ext.series_id != null && ext.series_id !== '') return true;
  if (ext.recurrence_series_id != null && ext.recurrence_series_id !== '') return true;
  return false;
}

interface IDeleteProps {
  open: boolean;
  close: () => void;
  deleteConsultation: (id: number, scope?: 'series' | 'this_and_future') => void;
  data: IConsultation | null;
  client?: Pick<IClient, 'id' | 'full_name' | 'time_zone'>;
  /** Удаление из календаря (для повторяющихся — отдельная модалка с выбором области) */
  isFromCalendar?: boolean;
}

export const DeleteConsultationForm: FC<IDeleteProps> = ({
  open,
  data,
  close,
  deleteConsultation,
  client,
  isFromCalendar = false,
}) => {
  const [deleteScope, setDeleteScope] = useState<DeleteScope>('one');

  const showRecurringCalendarDelete = isFromCalendar && data !== null && isRecurringConsultation(data);

  useEffect(() => {
    if (open && showRecurringCalendarDelete) setDeleteScope('one');
  }, [open, showRecurringCalendarDelete]);

  if (!data) return <></>;

  const handleDelete = () => {
    if (!data) return;
    if (showRecurringCalendarDelete && deleteScope === 'series') {
      deleteConsultation(data.id, 'series');
    } else if (showRecurringCalendarDelete && deleteScope === 'this_and_future') {
      deleteConsultation(data.id, 'this_and_future');
    } else {
      deleteConsultation(data.id);
    }
    close();
  };

  const footer = (
    <>
      <Button size="md" variant="neutral" onClick={close}>
        Отмена
      </Button>
      <Button size="md" variant="dark" onClick={handleDelete}>
        Удалить
      </Button>
    </>
  );

  if (showRecurringCalendarDelete) {
    return (
      <Modal isOpen={open} onClose={close} title="Удаление повторяющегося мероприятия" footer={footer}>
        <RadioGroup value={deleteScope} onChange={setDeleteScope}>
          <Radio value="one" label="Только это мероприятие" />
          <Radio value="this_and_future" label="Это и последующие мероприятия" />
          <Radio value="series" label="Все мероприятия" />
        </RadioGroup>
      </Modal>
    );
  }

  return (
    <Modal isOpen={open} onClose={close} title="Удаление консультации" footer={footer}>
      <div className="flex flex-col gap-4">
        <p className="text-ui-strong md:text-body-l-strong">
          Вы действительно хотите удалить консультацию {`«${client?.full_name}»`} ?
        </p>
        <div className="flex flex-col items-start gap-2 text-ui md:text-body-l">
          <div className="flex gap-4">
            <p className="text-ink-subtle">Дата встречи:</p>
            <p>{getDate(new Date(data.date))}</p>
          </div>
          <div className="flex gap-4">
            <p className="min-w-max text-ink-subtle">Ваше время:</p>
            <p>{`${getTime(new Date(data.datetime_start))} - ${getTime(new Date(data.datetime_end))} (${data.client_timezone})`}</p>
          </div>
        </div>
      </div>
    </Modal>
  );
};
